/**
 * Wayflyer Events — shared data with locale-specific display overrides
 *
 * Single source of truth for the /events page. Edit `WAYFLYER_EVENTS` to add,
 * update, or remove events. Events are grouped by the month of their `startDate`
 * and rendered in the order they appear here, so keep them in chronological order.
 *
 * Dates are ISO `YYYY-MM-DD` strings (parsed as calendar dates, no timezone).
 * `endDate` is optional — omit it for single-day events.
 *
 * For events without a confirmed date, set `startDate` to the first of the
 * expected month (so it groups correctly) and provide a `dateLabel` override
 * such as "Date TBD".
 */

import type { routing } from "@/i18n/routing";

type Locale = (typeof routing.locales)[number];

export interface WayflyerEvent {
  /** Stable ID for locale overrides and React keys. */
  id: string;
  /** Event name, shown as the (optionally linked) title. */
  name: string;
  /** Start date, ISO `YYYY-MM-DD`. Determines which month the event groups under. */
  startDate: string;
  /** End date, ISO `YYYY-MM-DD`. Omit for single-day events. */
  endDate?: string;
  /** Overrides the computed date range (e.g. "Date TBD"). Still groups by `startDate`. */
  dateLabel?: string;
  /** City / venue, shown next to the dates. */
  location?: string;
  /** Optional link to the event or a Wayflyer page. */
  url?: string;
  /** Set true when `url` points off the Wayflyer site (opens with external handling). */
  external?: boolean;
  /** Set true for events Wayflyer hosts (marks Wayflyer as the schema.org organizer). */
  hosted?: boolean;
  /** Short plain-text summary for schema.org `description`. */
  description?: string;
  /** schema.org `image` URL — falls back to the /events OG image. */
  image?: string;
  /** External organizer name for schema.org when Wayflyer is not the host. */
  organizer?: string;
  /** schema.org `performer` name — defaults to `organizer` or the event name. */
  performer?: string;
}

export const WAYFLYER_EVENTS = [
  {
    id: "summer-fancy-food-show",
    name: "Summer Fancy Food Show",
    description:
      "Specialty food and beverage trade show in New York where Wayflyer connects with CPG founders.",
    organizer: "Specialty Food Association",
    startDate: "2026-06-28",
    endDate: "2026-06-30",
    location: "New York",
    url: "https://www.specialtyfood.com/fancy-food-shows/summer/attend/",
    external: true,
  },
  {
    id: "not-so-fancy-happy-hour",
    name: "Not So Fancy Happy Hour",
    description:
      "Founder networking happy hour for food and beverage brands in New York.",
    organizer: "Wayflyer",
    startDate: "2026-06-28",
    location: "New York",
    url: "https://luma.com/ppa7csps",
    external: true,
  },
  {
    id: "food-girl-summer",
    name: "Food Girl Summer",
    description:
      "Community event for emerging food and beverage founders in New York.",
    organizer: "Wayflyer",
    startDate: "2026-06-30",
    location: "New York",
    url: "https://luma.com/9s5ouhp9?tk=K8L0PD",
    external: true,
  },
  {
    id: "bread-and-jam-fest",
    name: "Bread & Jam Fest",
    description: "Festival for independent food and drink brands in London.",
    organizer: "Bread & Jam",
    startDate: "2026-07-14",
    endDate: "2026-07-15",
    location: "London",
    url: "https://www.breadandjamfest.com/events/bread-jam-festival-2026",
    external: true,
  },
  {
    id: "swing-sessions-sydney-james",
    name: "Swing Sessions",
    description: "Brand integration session with Sydney James in New York.",
    organizer: "Sydney James",
    performer: "Sydney James",
    startDate: "2026-07-16",
    location: "New York",
    url: "https://shopsydneyjames.com/products/sj-swing-sessions-brand-integration",
    external: true,
  },
  {
    id: "from-cart-to-shelf",
    name: "From Cart to Shelf",
    description:
      "Panel and networking event for eCommerce brands expanding into retail.",
    organizer: "Wayflyer",
    startDate: "2026-07-29",
    location: "New York",
    url: "https://luma.com/rkqc0nug",
    external: true,
  },
  {
    id: "wayflyer-visionaries-summit",
    name: "Wayflyer Visionaries Summit",
    description:
      "Two-day summit for eCommerce founders featuring panels, pitch night, and networking in Brooklyn.",
    image:
      "https://cdn.sanity.io/images/1aendizt/redesign/8fc306006606a5d53e978d6c85d6da634030d49a-1200x630.png",
    startDate: "2026-08-26",
    endDate: "2026-08-27",
    location: "KidSuper Studios, Brooklyn",
    url: "/visionaries-summit",
    hosted: true,
  },
  {
    id: "ideas-fest",
    name: "Ideas Fest",
    description:
      "Entrepreneurship festival in Tring for founders building consumer brands.",
    organizer: "Ideas Fest",
    startDate: "2026-09-09",
    endDate: "2026-09-10",
    location: "Tring",
    url: "https://ideasfest.uk/whats-on",
    external: true,
  },
  {
    id: "myca-tasting-table",
    name: "Myca Presents: The Tasting Table",
    description:
      "Evening gathering at Pearl Box bringing together founders, funders and operators from across the CPG ecosystem in New York.",
    organizer: "Myca Collective",
    startDate: "2026-10-06",
    location: "Pearl Box, New York",
    url: "https://luma.com/lxpphxm2",
    external: true,
  },
  {
    id: "ny-ad-week-padel-social",
    name: "NY Ad Week Padel Social",
    description:
      "Morning padel social for the advertising and brand community during New York Advertising Week.",
    organizer: "The Nucleus Network",
    startDate: "2026-10-07",
    location: "New York",
    url: "https://www.therumor.com/p/882635",
    external: true,
  },
  {
    id: "league-of-originals-retreat",
    name: "League of Originals Retreat",
    description:
      "Retreat for DTC founders hosted by League of Originals in Los Angeles.",
    organizer: "League of Originals",
    startDate: "2026-10-21",
    endDate: "2026-10-22",
    location: "Los Angeles",
    url: "https://www.leagueoforiginals.com/upcoming",
    external: true,
  },
  {
    id: "league-of-originals-summit",
    name: "League of Originals Summit",
    description:
      "Summit for independent consumer brand founders in Los Angeles.",
    organizer: "League of Originals",
    startDate: "2026-10-23",
    location: "Los Angeles",
    url: "https://www.leagueoforiginals.com/summit25/la",
    external: true,
  },
  {
    id: "monday-girl-black-friday-edit",
    name: "Monday Girl x Wayflyer: The Black Friday Edit",
    description:
      "Evening event with Monday Girl at Maxwell Social for founders preparing for Black Friday in New York.",
    organizer: "Monday Girl",
    startDate: "2026-11-02",
    location: "Maxwell Social, New York",
    url: "https://luma.com/MGMaxwellNovember",
    external: true,
  },
  {
    id: "reset-before-the-rush",
    name: "Reset Before the Rush: Wayflyer x Lore",
    description:
      "Founder wellness reset at Lore Bathing Club ahead of the Black Friday rush in New York.",
    organizer: "Lore Bathing Club",
    startDate: "2026-11-05",
    location: "Lore Bathing Club, New York",
    url: "https://app.arketa.co/lorebathingclub/checkout/NsMOUM8nyiVCwz8LcvSl",
    external: true,
  },
  {
    id: "after-hours-van-leeuwen",
    name: "After Hours with Van Leeuwen Ice Cream: Scaling CPG operations in the Age of AI",
    description:
      "Operator-led evening with Kaizntree and Van Leeuwen Ice Cream on scaling operations, planning and AI automation for CPG brands in New York.",
    organizer: "Kaizntree",
    startDate: "2026-11-12",
    location: "New York",
    url: "https://luma.com/mrqx4h6e",
    external: true,
  },
  {
    id: "pre-black-friday-therapy-dinner",
    name: "Pre Black Friday Therapy Dinner",
    description:
      "Founder dinner in London to share notes and unwind before Black Friday.",
    organizer: "Wayflyer",
    startDate: "2026-11-19",
    location: "London",
    url: "https://luma.com/xwi0hy5l",
    external: true,
  },
] as const satisfies readonly WayflyerEvent[];

type WayflyerEventId = (typeof WAYFLYER_EVENTS)[number]["id"];

type LocalizedEventFields = Partial<
  Pick<WayflyerEvent, "dateLabel" | "location">
>;

type EventLocalizations = Partial<
  Record<Locale, Partial<Record<WayflyerEventId, LocalizedEventFields>>>
>;

const EVENT_LOCALIZATIONS: EventLocalizations = {
  es: {
    "summer-fancy-food-show": { location: "Nueva York" },
    "not-so-fancy-happy-hour": { location: "Nueva York" },
    "food-girl-summer": { location: "Nueva York" },
    "bread-and-jam-fest": { location: "Londres" },
    "swing-sessions-sydney-james": { location: "Nueva York" },
    "from-cart-to-shelf": { location: "Nueva York" },
    "myca-tasting-table": { location: "Pearl Box, Nueva York" },
    "ny-ad-week-padel-social": { location: "Nueva York" },
    "league-of-originals-retreat": { location: "Los Ángeles" },
    "league-of-originals-summit": { location: "Los Ángeles" },
    "monday-girl-black-friday-edit": { location: "Maxwell Social, Nueva York" },
    "reset-before-the-rush": { location: "Lore Bathing Club, Nueva York" },
    "after-hours-van-leeuwen": { location: "Nueva York" },
    "pre-black-friday-therapy-dinner": { location: "Londres" },
  },
  nl: {
    "bread-and-jam-fest": { location: "Londen" },
    "pre-black-friday-therapy-dinner": { location: "Londen" },
  },
};

/**
 * Latest timezone among event locations, so an event stays listed through the
 * end of its final day everywhere before dropping off the page.
 */
const ARCHIVE_TIME_ZONE = "America/Los_Angeles";

/** Today as ISO `YYYY-MM-DD` (en-CA locale formats dates in ISO order). */
function todayIso(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: ARCHIVE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/**
 * Returns upcoming and in-progress events for a locale. Past events are
 * auto-archived: an event drops off the day after its `endDate` (or
 * `startDate` for single-day events). The events page revalidates daily so
 * this filter re-runs without a deploy.
 */
export function getWayflyerEventsForLocale(locale: Locale): WayflyerEvent[] {
  const localizations = EVENT_LOCALIZATIONS[locale] ?? {};
  const today = todayIso();

  return WAYFLYER_EVENTS.filter(
    (event: WayflyerEvent) => (event.endDate ?? event.startDate) >= today
  ).map((event) => ({
    ...event,
    ...localizations[event.id],
  }));
}
